import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft'
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo'
import Button from '@space-core/components/button'
import Loader from '@space-core/components/loader'
import { Actions, Entities } from '@space-core/datahub'
import { useMediaQuery } from '@space-core/hooks'
import { ScreenSizes } from '@space-core/styles'

import Icon from '@space-shell/common/components/icon'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { RoleType } from '@space-shell/core/store'
import { ViewAsFromPageType, useMembershipStore, useViewAsStore } from '@space-shell/core/store'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import styled from 'styled-components'

import { ClassButton } from './classButton'
import { RoleButton } from './roleButton'
import UnavailableMessageButton from './unavailableMessageButton'

const Wrapper = styled.div`
  top: 0;
  left: 0;
  z-index: 10;
  max-height: 5.125rem;
  max-width: 100vw;
`

export const ROLES_WITH_CLASS: Omit<RoleType, 'UserLogout'>[] = [
  RoleType.Creator,
  RoleType.Member,
  RoleType.UserNoAccess
]

const ViewAsBar = () => {
  const { t } = useTranslation()
  const { productId } = useParams<{ productId: string }>()
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.lg})`)
  const { membershipSlug } = useMembershipStore()
  const { role, from, isReadonly } = useViewAsStore()
  const { sendDataHubEvent } = useDataHubEvents()
  const { authStatus } = useAuthContext()

  const showClassButton = ROLES_WITH_CLASS.includes(role) && Boolean(productId) && !isReadonly

  const handleSettingsClick = () => {
    sendDataHubEvent({ group: Entities.HEADER_VIEW_AS, name: Actions.CLICK_VIEW_AS_SETTINGS })

    const membershipAdminUrl = `${process.env.MEMBERSHIP_ADMIN_URL}/${membershipSlug}`
    const productsUrl = `${membershipAdminUrl}/products`
    const FromUrls: { [keys in ViewAsFromPageType]: string } = {
      [ViewAsFromPageType.Home]: membershipAdminUrl,
      [ViewAsFromPageType.ListProducts]: productsUrl,
      [ViewAsFromPageType.EditProduct]: productId
        ? `${productsUrl}/edit/${productId}`
        : membershipAdminUrl
    }

    const targetUrl = FromUrls[from]
    window.open(targetUrl, '_self')
  }

  if (authStatus === AuthStatus.AUTHENTICATING) {
    return <Loader full />
  }

  return (
    <Wrapper className='_bg-primary _position-fixed _w-full _h-full _p-2 _d-flex _align-items-center'>
      <div
        title={t('view_as.title')}
        className='_bg-gray-100 _d-flex _justify-content-between _align-items-center _rounded _w-full _py-2 _px-4'>
        <Button variation='default' className='_border-0 _mr-2' onClick={handleSettingsClick}>
          <Icon icon={faChevronLeft} className='_mr-md-2' />
          {!isMobile && t('general.settings')}
        </Button>

        {isReadonly && !isMobile && (
          <div className='_text-gray-700'>
            <Icon icon={faCircleInfo} />
            <span className='_ml-2'>{t('view_as.unavailable_message')}</span>
          </div>
        )}

        <div className='_d-flex _justify-content-center _align-items-center _text-truncate'>
          {isReadonly && isMobile && <UnavailableMessageButton className='_mr-2' />}

          <div className='_d-flex _align-items-center _justify-content-end _text-truncate'>
            <RoleButton />
            {showClassButton && <ClassButton className='_ml-2 _ml-sm-4 _ml-md-6' />}
          </div>
        </div>
      </div>
    </Wrapper>
  )
}

export default ViewAsBar
